/**
 * Your own name, password and appearance.
 *
 * The one tab everybody has, whatever their role.
 */
import { useState } from 'react';
import { Button, Card, Group, PasswordInput, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { changePassword, saveProfile } from '../../api/gateway';
import { useAuth } from '../../auth/AuthProvider';
import { APPEARANCE_OPTIONS, isAppearance, useAppearance } from '../../appearance';

export function ProfileTab() {
  const { user } = useAuth();
  const appearance = useAppearance();
  const [first, setFirst] = useState(user?.first_name ?? '');
  const [last, setLast] = useState(user?.last_name ?? '');

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const profile = useMutation({
    mutationFn: () => saveProfile(first.trim(), last.trim()),
    onSuccess: () =>
      notifications.show({ color: 'teal', title: 'Saved', message: 'Your name has been updated.' }),
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  const password = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      notifications.show({
        color: 'teal',
        title: 'Password changed',
        message: 'Use the new one next time you sign in.',
      });
    },
    onError: (e) =>
      notifications.show({
        color: 'red',
        title: 'Could not change the password',
        message: (e as Error).message,
      }),
  });

  // Checked here as well as by the server, so the mismatch is caught before a
  // round trip rather than after it.
  const mismatch = confirm !== '' && next !== confirm;
  const canChange = current !== '' && next.length >= 8 && next === confirm;

  return (
    <Stack>
      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Text fw={600}>Your details</Text>
          <Text size="sm" c="dimmed">Signed in as {user?.email}</Text>
          <Group grow>
            <TextInput
              label="First name"
              value={first}
              onChange={(e) => setFirst(e.currentTarget.value)}
            />
            <TextInput
              label="Last name"
              value={last}
              onChange={(e) => setLast(e.currentTarget.value)}
            />
          </Group>
          <Group justify="flex-end">
            <Button
              loading={profile.isPending}
              disabled={first === (user?.first_name ?? '') && last === (user?.last_name ?? '')}
              onClick={() => profile.mutate()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack gap="sm">
          <div>
            <Text fw={600}>Appearance</Text>
            <Text size="sm" c="dimmed">
              Saved to your account, so it follows you to other browsers and the desktop app.
            </Text>
          </div>
          <SegmentedControl
            aria-label="Appearance"
            data={APPEARANCE_OPTIONS}
            value={appearance.value}
            onChange={(v) => { if (isAppearance(v)) appearance.set(v); }}
            style={{ alignSelf: 'flex-start' }}
          />
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Stack>
          <Text fw={600}>Change your password</Text>
          <PasswordInput
            label="Current password"
            value={current}
            onChange={(e) => setCurrent(e.currentTarget.value)}
          />
          <PasswordInput
            label="New password"
            description="At least eight characters."
            value={next}
            onChange={(e) => setNext(e.currentTarget.value)}
          />
          <PasswordInput
            label="New password again"
            error={mismatch ? 'These do not match' : undefined}
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              loading={password.isPending}
              disabled={!canChange}
              onClick={() => password.mutate()}
            >
              Change password
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
